import React from 'react';
import styled, { css } from 'styled-components';
import { IAnimationProps, useRotateAnimationCss } from '../../fundamentals/animations/animations';
import { BASIC_COLOURS, MAIN_BRAND_COLOURS, STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterProps } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IParagraphSize, P } from '../Texts/Texts';
import { ReactComponent as SpinnerForeground } from './SpinnerForeground.svg';
import { ReactComponent as SpinnerBackground } from './SpinnerBackground.svg';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { Heading6 } from '../Headings/Headings';

export type LoadingAnimationSize = typeof SIZES.MICRO | typeof SIZES.XXX_SMALL | typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.LARGE;

export const LOADING_ANIMATION_VARIANTS = {
  PRIMARY: MAIN_BRAND_COLOURS.PRIMARY,
  ...BASIC_COLOURS,
  NEUTRAL: STATUS_COLOURS.NEUTRAL
} as const;

export type LoadingAnimationVariant = (typeof LOADING_ANIMATION_VARIANTS)[keyof typeof LOADING_ANIMATION_VARIANTS];
export type LoadingAnimationLabelPosition = 'right' | 'below' | 'none';

export interface ILoadingAnimationProps extends IGutterProps {
  label?: string;
  labelPosition?: LoadingAnimationLabelPosition;
  size?: LoadingAnimationSize;
  variant?: LoadingAnimationVariant;
  /**
   * @remarks Intended for internal use by other component such as Button. Prefer variant
   */
  customVariant?: string;
}

export const loadingAnimationColours = ({
  primary,
  base,
  palette,
  text,
  eoraCoreColours
}: IColoursCollection): { loadingAnimationColours: Record<LoadingAnimationVariant, ColourAssignment> } => ({
  loadingAnimationColours: {
    [LOADING_ANIMATION_VARIANTS.PRIMARY]: { background: primary.colour, text: text.default },
    [LOADING_ANIMATION_VARIANTS.BLACK]: { background: base.black.colour, text: base.black.colour },
    [LOADING_ANIMATION_VARIANTS.WHITE]: { background: eoraCoreColours.BASE.WHITE_NOSWITCH, text: eoraCoreColours.BASE.WHITE_NOSWITCH },
    [LOADING_ANIMATION_VARIANTS.NEUTRAL]: { background: palette.neutral, text: palette.neutral }
  }
});

const labelParagraphSize: Partial<Record<LoadingAnimationSize, IParagraphSize>> = {
  [SIZES.MICRO]: 'MICRO',
  [SIZES.XXX_SMALL]: 'MICRO',
  [SIZES.X_SMALL]: 'SMALL',
  [SIZES.SMALL]: 'DEFAULT'
};

const LoadingAnimationContainer = styled(GapContainer)<ILoadingAnimationProps>`
  flex-direction: ${({ labelPosition }) => (labelPosition === 'below' ? 'column' : 'row')};
  align-items: center;
  ${({ theme, size, labelPosition }) =>
    size === 'large'
      ? css`
          gap: ${theme.eoraSpaces.offgrid_12};
        `
      : css`
          gap: ${labelPosition === 'below' ? theme.eoraSpaces.small : theme.eoraSpaces.medium};
        `};
`;

const SvgContainer = styled(BaseDiv)<Required<Pick<ILoadingAnimationProps, 'size'>> & Pick<IAnimationProps, 'useRotate'>>`
  display: flex;
  ${useRotateAnimationCss};
  ${({ theme, size }) => css`
    width: ${theme.sizes[size]};
    height: ${theme.sizes[size]};
  `};
`;

// Note: custom is customVariant on ILoadingAnimationProps, however browser will complain "React does not recognize the `customVariant` prop on a DOM element" if uppercase
type SpinnerColourProps = Required<Pick<ILoadingAnimationProps, 'variant'>> & { custom?: string };

const useSpinnerCss = () => css<SpinnerColourProps>`
  position: absolute;
  width: 100%;
  height: 100%;
  & > * {
    width: 100%;
    height: 100%;
    fill: ${({ theme, custom, variant }) => custom ?? theme.loadingAnimationColours[variant].background};
  }
`;

const useLabelCss = () => css<SpinnerColourProps>`
  color: ${({ theme, variant }) => theme.loadingAnimationColours[variant].text};
`;

const SpinnerForegroundSvg = styled(SpinnerForeground)<SpinnerColourProps>`
  ${useSpinnerCss()}
`;

const SpinnerBackgroundSvg = styled(SpinnerBackground)<SpinnerColourProps>`
  ${useSpinnerCss()}
`;

const ColourHeading = styled(Heading6)<SpinnerColourProps>`
  ${useLabelCss()}
`;

const ColourP = styled(P)<SpinnerColourProps>`
  ${useLabelCss()}
`;

const LoadingAnimationLabel = ({ size, variant, label, labelPosition, ...rest }: ILoadingAnimationProps) => {
  const { generateTestId } = useTestId(rest, 'LoadingAnimationLabel');
  if (!label || labelPosition === 'none') return <></>;
  return size === 'large' ? (
    <ColourHeading headingType='REGULAR' gutter={false} variant={variant!} {...generateTestId()}>
      {label}
    </ColourHeading>
  ) : (
    <ColourP textEoraSize={labelParagraphSize[size!]} textStyle='REGULAR' gutter={false} variant={variant!} {...generateTestId()}>
      {label}
    </ColourP>
  );
};

export const LoadingAnimation: React.FC<ILoadingAnimationProps> = ({
  size = SIZES.X_SMALL,
  variant = LOADING_ANIMATION_VARIANTS.NEUTRAL,
  customVariant,
  labelPosition = 'right',
  label,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'LoadingAnimation');

  return (
    <LoadingAnimationContainer {...generateTestId()} labelPosition={labelPosition} size={size} {...rest}>
      <SvgContainer useRotate size={size} {...generateTestId('Spinner')}>
        <SpinnerForegroundSvg variant={variant} custom={customVariant} {...rest} {...generateTestId('Spinner.Background')} />
        <SpinnerBackgroundSvg variant={variant} custom={customVariant} {...rest} {...generateTestId('Spinner.Foreground')} />
      </SvgContainer>
      <LoadingAnimationLabel labelPosition={labelPosition} size={size} variant={variant} label={label} {...generateTestId('Label')} />
    </LoadingAnimationContainer>
  );
};
